import { Code } from "@connectrpc/connect";
import { useQuery } from "@connectrpc/connect-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { nepalToday } from "@yatayat/core/date";
import { StaffRole } from "@yatayat/gen/yatayat/auth/v1/auth_pb";
import { ReportService } from "@yatayat/gen/yatayat/operator/v1/reports_pb";

import { BsDateHint } from "~/components/BsDateHint";
import { DailyReport, DailyReportPrint, reportDateLabel } from "~/components/DailyReport";
import { useStaffSessionExpiry } from "~/components/OperatorTrip";
import { Loading, QueryError } from "~/components/QueryState";
import { useI18n } from "~/lib/i18n";
import { printPage } from "~/lib/native";
import { errorCode, retryTransient } from "~/lib/rpc/errors";
import { searchString } from "~/lib/search";
import { can, useStaffSession } from "~/lib/staff";
import { ui } from "~/lib/ui";

type ReportSearch = { date?: string };

// The date lives in the URL so a report for a past day can be reopened or shared.
export const Route = createFileRoute("/operator/report")({
  validateSearch: (search: Record<string, unknown>): ReportSearch => ({
    date: searchString(search.date),
  }),
  component: OperatorReport,
});

const isoDate = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;

function OperatorReport() {
  const { t, lang } = useI18n();
  const session = useStaffSession().session;
  const allowed = can.viewReport(session?.role ?? StaffRole.UNSPECIFIED);
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const today = nepalToday();
  const date = search.date && isoDate.test(search.date) ? search.date : today;
  // Today's figures move with every sale; a past day's change only with a late cancellation.
  const report = useQuery(
    ReportService.method.getDailyReport,
    { date },
    { enabled: allowed, retry: retryTransient, refetchInterval: date === today ? 30_000 : false },
  );

  const unauthenticated = useStaffSessionExpiry(report.error);
  const denied = report.isError && errorCode(report.error) === Code.PermissionDenied;

  if (!allowed || denied) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <h1 className={ui.h1}>{t("reportTitle")}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t("reportNotAllowed")}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className={ui.h1}>{t("reportTitle")}</h1>
          <p className="text-slate-600 dark:text-slate-400">{reportDateLabel(date, lang)}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t("serviceDate")}</span>
            <input
              type="date"
              value={date}
              max={today}
              onChange={(e) => void navigate({ search: { date: e.target.value || undefined }, replace: true, resetScroll: false })}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
            />
            <BsDateHint date={date} />
          </label>
          {report.data && (
            <button
              type="button"
              onClick={printPage}
              className="rounded-md bg-ink px-4 py-2 text-sm font-medium text-white dark:bg-ink-dark dark:text-slate-950"
            >
              {t("reportPrint")}
            </button>
          )}
        </div>
      </div>

      {/* Not a live region: today's figures refresh every 30 seconds. */}
      <div>
        {report.isPending && <Loading />}
        {report.isError && !report.data && !unauthenticated && <QueryError onRetry={() => void report.refetch()} busy={report.isFetching} />}
        {report.data && (
          <>
            <DailyReport report={report.data} />
            <DailyReportPrint report={report.data} date={date} operatorName={session?.operatorName?.[lang] ?? ""} />
          </>
        )}
      </div>
    </div>
  );
}
